export type LeaveStatus = "Approved" | "Pending" | "Rejected";
export type LeaveType = "Casual Leave" | "Sick Leave" | "Earned Leave" | "Compensatory Off" | "Unpaid Leave";

export interface LeaveRequest {
  id: number;
  empId: number;
  empName: string;
  empAvatar: string;
  department: string;
  type: LeaveType;
  from: string;
  to: string;
  days: number;
  reason: string;
  status: LeaveStatus;
  appliedOn: string;
  approvedBy?: string;
}

export interface PublicHoliday {
  id: number;
  name: string;
  date: string;
  type: "National" | "Regional" | "Optional";
}

export const mockLeaveRequests: LeaveRequest[] = [
  {
    id: 1, empId: 5, empName: "Mahesh Babu", empAvatar: "MB", department: "Field Operations",
    type: "Sick Leave", from: "2026-04-14", to: "2026-04-18", days: 5,
    reason: "Fever and medical rest advised by doctor.", status: "Approved",
    appliedOn: "2026-04-13", approvedBy: "Priya Sharma",
  },
  {
    id: 2, empId: 1, empName: "Ravi Teja", empAvatar: "RT", department: "Field Operations",
    type: "Casual Leave", from: "2026-04-25", to: "2026-04-25", days: 1,
    reason: "Personal work.", status: "Pending",
    appliedOn: "2026-04-20",
  },
  {
    id: 3, empId: 7, empName: "Amit Patel", empAvatar: "AP", department: "Office",
    type: "Earned Leave", from: "2026-05-01", to: "2026-05-03", days: 3,
    reason: "Family function out of station.", status: "Approved",
    appliedOn: "2026-04-18", approvedBy: "Priya Sharma",
  },
  {
    id: 4, empId: 9, empName: "Lakshmi Devi", empAvatar: "LD", department: "Office",
    type: "Casual Leave", from: "2026-04-22", to: "2026-04-22", days: 1,
    reason: "Personal errand.", status: "Pending",
    appliedOn: "2026-04-19",
  },
  {
    id: 5, empId: 3, empName: "Kiran Babu", empAvatar: "KB", department: "Field Operations",
    type: "Compensatory Off", from: "2026-04-28", to: "2026-04-28", days: 1,
    reason: "Worked on Sunday 2026-04-12 for urgent survey.", status: "Approved",
    appliedOn: "2026-04-15", approvedBy: "Priya Sharma",
  },
  {
    id: 6, empId: 2, empName: "Suresh Kumar", empAvatar: "SK", department: "Field Operations",
    type: "Sick Leave", from: "2026-04-10", to: "2026-04-11", days: 2,
    reason: "Stomach infection.", status: "Approved",
    appliedOn: "2026-04-10", approvedBy: "Priya Sharma",
  },
  {
    id: 7, empId: 8, empName: "Sanjay Drone", empAvatar: "SD", department: "Field Operations",
    type: "Casual Leave", from: "2026-05-05", to: "2026-05-06", days: 2,
    reason: "Attending cousin wedding.", status: "Pending",
    appliedOn: "2026-04-21",
  },
  {
    id: 8, empId: 10, empName: "Venkat Reddy", empAvatar: "VR", department: "Lab",
    type: "Unpaid Leave", from: "2026-04-07", to: "2026-04-08", days: 2,
    reason: "Emergency travel.", status: "Rejected",
    appliedOn: "2026-04-06",
  },
  {
    id: 9, empId: 6, empName: "Priya Sharma", empAvatar: "PS", department: "Management",
    type: "Earned Leave", from: "2026-05-12", to: "2026-05-14", days: 3,
    reason: "Annual vacation.", status: "Approved",
    appliedOn: "2026-04-20", approvedBy: "Director",
  },
  {
    id: 10, empId: 4, empName: "Naresh Yadav", empAvatar: "NY", department: "Field Operations",
    type: "Casual Leave", from: "2026-04-30", to: "2026-04-30", days: 1,
    reason: "Personal work.", status: "Pending",
    appliedOn: "2026-04-21",
  },
];

export const mockPublicHolidays: PublicHoliday[] = [
  { id: 1, name: "Dr. Ambedkar Jayanti", date: "2026-04-14", type: "National" },
  { id: 2, name: "Good Friday", date: "2026-04-03", type: "National" },
  { id: 3, name: "Maharashtra Day", date: "2026-05-01", type: "Regional" },
  { id: 4, name: "Eid ul-Fitr", date: "2026-04-21", type: "National" },
  { id: 5, name: "Independence Day", date: "2026-08-15", type: "National" },
  { id: 6, name: "Gandhi Jayanti", date: "2026-10-02", type: "National" },
  { id: 7, name: "Diwali", date: "2026-10-20", type: "National" },
  { id: 8, name: "Christmas", date: "2026-12-25", type: "National" },
  { id: 9, name: "Ugadi", date: "2026-03-19", type: "Regional" },
  { id: 10, name: "Sankranti", date: "2026-01-14", type: "Regional" },
  { id: 11, name: "Republic Day", date: "2026-01-26", type: "National" },
  { id: 12, name: "Holi", date: "2026-03-03", type: "National" },
];

export const leaveBalances: Record<number, { casual: number; sick: number; earned: number; compOff: number }> = {
  1: { casual: 8, sick: 10, earned: 15, compOff: 2 },
  2: { casual: 9, sick: 8, earned: 12, compOff: 0 },
  3: { casual: 7, sick: 10, earned: 18, compOff: 3 },
  4: { casual: 10, sick: 10, earned: 5, compOff: 0 },
  5: { casual: 5, sick: 5, earned: 10, compOff: 0 },
  6: { casual: 10, sick: 10, earned: 20, compOff: 1 },
  7: { casual: 9, sick: 9, earned: 14, compOff: 2 },
  8: { casual: 10, sick: 10, earned: 8, compOff: 0 },
  9: { casual: 8, sick: 10, earned: 13, compOff: 1 },
  10: { casual: 10, sick: 10, earned: 6, compOff: 0 },
};
